import { ApolloClient, ApolloError, InMemoryCache } from '@apollo/client';
import { GetServerSideProps } from 'next';
import * as React from 'react';

import { MilleFeuille, MilleFeuilleMain } from '@/layouts/MilleFeuille';
import {
  getWebflowHybridPageProps,
  WebflowHybridPageProps,
} from '@/lib/webflow-poc';
import { WebflowPageShell } from '@/lib/webflow-poc/WebflowPageShell';
import { applyQueryTokensForSsr } from '@/modules/accounts/applyQueryTokensForSsr';
import { FooterRender, PageRender } from '@/modules/website-builder';
import { PAGE_AND_TENANT } from '@/queries';
import {
  PageAndTenant,
  PageAndTenantVariables,
} from '@/queries/graphql-types/PageAndTenant';
import { TradewingServerErrorCode } from '@/utilities/tradewing-server-error-code';

type CmsSlugPageProps = {
  mode: 'cms';
  content: React.ComponentProps<typeof PageRender>['content'];
  tenant: React.ComponentProps<typeof FooterRender>['tenant'];
};

type WebflowSlugPageProps = {
  mode: 'webflow';
  webflow: WebflowHybridPageProps['webflow'];
  navigationChrome: WebflowHybridPageProps['navigationChrome'];
  tenant: WebflowHybridPageProps['tenant'];
};

type SlugPageProps = CmsSlugPageProps | WebflowSlugPageProps;

type TenantWithWebflowFlag = PageAndTenant['tenant']['result'] & {
  featureFlags?: { webflow?: boolean | null } | null;
};

const normalizeSlugParam = (
  slugParam: string | string[] | undefined
): string | null => {
  if (!slugParam) {
    return null;
  }
  // Catch-all routes pass `string[]` (e.g. ["advertise"] or ["a", "b"]).
  // `.toString()` would incorrectly join nested segments with commas.
  if (Array.isArray(slugParam)) {
    const joined = slugParam.filter(Boolean).join('/');
    return joined || null;
  }
  return slugParam || null;
};

const isSlugNotFoundError = (error: unknown): boolean => {
  if (!(error instanceof ApolloError)) {
    return false;
  }
  return error.graphQLErrors.some(
    (e) => e.extensions?.code === TradewingServerErrorCode.SlugNotFound
  );
};

const isUnknownTenantError = (error: unknown): boolean => {
  return (
    (error as { networkError?: { statusCode?: number } })?.networkError
      ?.statusCode === 422
  );
};

/** Next.js requires serializable GSSP props; strip non-JSON values safely. */
const toSerializableProps = <T,>(value: T): T =>
  JSON.parse(JSON.stringify(value)) as T;

const createSsrApolloClient = () =>
  new ApolloClient({
    ssrMode: true,
    uri: process.env.NEXT_PUBLIC_APOLLO_HTTP_URI,
    cache: new InMemoryCache(),
  });

const fetchPageAndTenant = async (
  context: Parameters<GetServerSideProps>[0],
  slug: string
) => {
  const client = createSsrApolloClient();
  return client.query<PageAndTenant, PageAndTenantVariables>({
    query: PAGE_AND_TENANT,
    context: {
      headers: {
        tenant_domain: context.req?.headers.tenant_domain,
        authorization: `Bearer ${context.req.cookies['accounts%3AaccessToken']}`,
      },
    },
    fetchPolicy: 'no-cache',
    // Keep partial data (tenant / page) even when nested field resolvers fail,
    // so a flaky imageProxy (etc.) cannot take down the whole Website page.
    errorPolicy: 'all',
    variables: {
      input: {
        id: slug,
        isSlug: true,
      },
    },
  });
};

/**
 * One retry for transient GraphQL/network blips. Website SSR previously
 * re-threw these and produced intermittent 500 "Unexpected Error" pages.
 */
const fetchPageAndTenantWithRetry = async (
  context: Parameters<GetServerSideProps>[0],
  slug: string
) => {
  try {
    return await fetchPageAndTenant(context, slug);
  } catch (error) {
    if (isSlugNotFoundError(error) || isUnknownTenantError(error)) {
      throw error;
    }
    console.warn('[site-slug] CMS query failed; retrying once', {
      slug,
      resolvedUrl: context.resolvedUrl,
      message: error instanceof Error ? error.message : String(error),
    });
    return fetchPageAndTenant(context, slug);
  }
};

export const getServerSideProps: GetServerSideProps<SlugPageProps> = async (
  context
) => {
  // Mobile login bypass: honor ?accessToken=&refreshToken= during SSR.
  applyQueryTokensForSsr(context);

  const slug = normalizeSlugParam(context.params?.slug);

  if (!slug) {
    return {
      notFound: true,
    };
  }

  try {
    const query = await fetchPageAndTenantWithRetry(context, slug);

    const graphQLErrors = query.errors ?? [];
    const slugNotFound = graphQLErrors.some(
      (e) => e.extensions?.code === TradewingServerErrorCode.SlugNotFound
    );

    // Partial GraphQL errors (e.g. a nested image field) used to throw here
    // and surface as random 500s even when page + tenant data were present.
    // Log and continue so classic Website Builder can still render.
    if (graphQLErrors.length && !slugNotFound) {
      console.warn('[site-slug] ignoring non-fatal GraphQL errors', {
        slug,
        resolvedUrl: context.resolvedUrl,
        codes: graphQLErrors.map((e) => e.extensions?.code ?? e.message),
      });
    }

    const tenant = (query.data?.tenant?.result ??
      null) as TenantWithWebflowFlag | null;

    // Webflow is consulted ONLY when featureFlags.webflow === true.
    // Tenants without Webflow (e.g. LGBTMPA) never enter this block —
    // zero Webflow GraphQL/upstream calls; classic Website Builder only.
    if (tenant?.featureFlags?.webflow === true) {
      try {
        const hybrid = await getWebflowHybridPageProps(context, {
          skipFeatureFlagCheck: true,
        });

        if (hybrid.kind === 'ok') {
          return {
            props: toSerializableProps({
              mode: 'webflow' as const,
              webflow: hybrid.props.webflow,
              navigationChrome: hybrid.props.navigationChrome,
              tenant: hybrid.props.tenant,
            }),
          };
        }

        // Any other hybrid outcome falls through to Website Builder below.
        if (hybrid.kind === 'error') {
          console.error(
            '[webflow-hybrid] site slug error, falling back to CMS',
            {
              resolvedUrl: context.resolvedUrl,
              statusCode: hybrid.statusCode,
            }
          );
        }
      } catch (webflowError) {
        console.error(
          '[webflow-hybrid] site slug unexpected failure, falling back to CMS',
          {
            resolvedUrl: context.resolvedUrl,
            message:
              webflowError instanceof Error
                ? webflowError.message
                : String(webflowError),
          }
        );
      }
    }

    // Classic Website Builder path (unchanged for non-Webflow tenants).
    if (slugNotFound || !query.data?.page?.content || !tenant) {
      return {
        notFound: true,
      };
    }

    return {
      props: {
        mode: 'cms',
        content: query.data.page.content,
        tenant,
      },
    };
  } catch (error) {
    if (isSlugNotFoundError(error) || isUnknownTenantError(error)) {
      return {
        notFound: true,
      };
    }

    // Soft-fail like the homepage: never rethrow into a 500 "Unexpected
    // Error" page for transient SSR failures. Callers can refresh.
    console.error('[site-slug] SSR failed after retry', {
      slug,
      resolvedUrl: context.resolvedUrl,
      message: error instanceof Error ? error.message : String(error),
    });

    return {
      notFound: true,
    };
  }
};

const SlugPage: React.FC<SlugPageProps> = (props) => {
  if (props.mode === 'webflow') {
    return (
      <WebflowPageShell
        webflow={props.webflow}
        navigationChrome={props.navigationChrome}
        tenant={props.tenant}
      />
    );
  }

  return (
    <MilleFeuille>
      <MilleFeuilleMain>
        <PageRender content={props.content} />
      </MilleFeuilleMain>
      <FooterRender tenant={props.tenant} />
    </MilleFeuille>
  );
};

export default SlugPage;
